// ============================================================================
// Trainer-side nutrition hooks (Wave 2) — read-only observation of a client's
// intake + targets.
// ----------------------------------------------------------------------------
//   • useClientNutrition  — one client's auto targets + day's meals/totals/water
//     (the client-detail Nutrition panel).
//   • useMealCalendar     — per-day meal markers across every client, merged as
//     the amber dots on the trainer calendar grid.
// Both live-sync: a client logging a meal/water broadcasts `meal`/`water`, which
// the live-sync layer maps (ENTITY_INVALIDATIONS) to the nutrition root, so the
// trainer's panel + calendar update without a refresh.
// ============================================================================
import { useQuery } from "@tanstack/react-query";
import { nutritionApi, qk } from "../api";
import type { MealCalendarRange } from "../api/nutrition";

/** Today's local date 'YYYY-MM-DD' (the day filter the nutrition panel uses). */
export function todayDayLocal(): string {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

/** One owned client's nutrition: auto targets + day's meals/totals + water. */
export function useClientNutrition(
  clientId: number | undefined,
  day?: string,
) {
  return useQuery({
    queryKey: qk.nutrition.forClient(clientId ?? -1, day),
    queryFn: ({ signal }) =>
      nutritionApi.forClient(clientId as number, day, signal),
    enabled: clientId !== undefined && clientId >= 0,
  });
}

/** Per-day meal markers across this trainer's clients (amber calendar dots). */
export function useMealCalendar(range: MealCalendarRange = {}) {
  return useQuery({
    queryKey: qk.nutrition.calendar(range.start, range.end),
    queryFn: ({ signal }) => nutritionApi.calendar(range, signal),
  });
}
